import { useState, useEffect } from "react";
import Dialog from "./Dialog";
import api from "../../services/api";
import { useToast } from "../providers/ToastProvider";

interface CourseCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (course: any) => void;
  onUpdate?: (course: any) => void;
  initialData?: any | null;
}

const getYoutubeId = (url: string) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\n]+)/);
  return match ? match[1] : null;
};

export default function CourseCreator({ isOpen, onClose, onCreate, onUpdate, initialData }: CourseCreatorProps) {
  const { showToast } = useToast();
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    publishDate: new Date().toISOString().split('T')[0],
    videoLink: "",
    highlightsLink: "",
    categoryId: "",
    topics: [] as string[],
    learning_order: "",
    estimated_duration_minutes: "",
    difficulty_level: "beginner",
    what_youll_learn: [""] as string[],
    feedback_links: [] as string[],
    access_tags: [] as number[]
  });

  const [availableTopics, setAvailableTopics] = useState<string[]>([]);
  const [availablePlans, setAvailablePlans] = useState<{ id: number; name: string }[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [quickTopicInput, setQuickTopicInput] = useState("");

  // Sync form with initialData when modal opens in edit mode
  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({
        title: initialData.title || "",
        description: initialData.description || "",
        publishDate: initialData.publishDate || "",
        videoLink: initialData.videoLink || "",
        highlightsLink: initialData.highlightsLink || "",
        categoryId: initialData.categoryId || "",
        topics: initialData.topics || [],
        learning_order: initialData.learning_order || "",
        estimated_duration_minutes: initialData.estimated_duration_minutes || "",
        difficulty_level: initialData.difficulty_level || "beginner",
        what_youll_learn: (initialData.what_youll_learn && initialData.what_youll_learn.length > 0) ? initialData.what_youll_learn : [""],
        feedback_links: initialData.feedback_links || [],
        access_tags: initialData.access_tags || []
      });
    } else if (!initialData && isOpen) {
      // Clear form for new course
      setFormData({ 
        title: "", 
        description: "", 
        publishDate: new Date().toISOString().split('T')[0], 
        videoLink: "", 
        highlightsLink: "", 
        categoryId: "", 
        topics: [],
        learning_order: "",
        estimated_duration_minutes: "",
        difficulty_level: "beginner",
        what_youll_learn: [""],
        feedback_links: [],
        access_tags: []
      });

      // Auto-fetch next learning order
      api.get("/courses")
        .then(res => {
          if (Array.isArray(res.data) && res.data.length > 0) {
            const maxOrder = Math.max(...res.data.map((c: any) => parseFloat(c.learning_order || 0)));
            if (!isNaN(maxOrder)) {
              setFormData(prev => ({ ...prev, learning_order: (maxOrder + 1).toString() }));
            }
          } else {
            setFormData(prev => ({ ...prev, learning_order: "1" }));
          }
        })
        .catch(err => console.error("Failed to fetch next learning order:", err));
    }
  }, [initialData, isOpen]);

  // Fetch live topics from registry
  useEffect(() => {
    if (isOpen) {
      api.get("/topics")
        .then(res => {
          const data = Array.isArray(res.data) ? res.data : [];
          setAvailableTopics(data.map((t: any) => t.name));
        })
        .catch(err => console.error("Failed to fetch topics:", err));
        
      api.get("/categories")
        .then(res => {
          const data = Array.isArray(res.data) ? res.data : [];
          setCategories(data);
        })
        .catch(err => console.error("Failed to fetch categories:", err));

      api.get("/admin/subscription-plans")
        .then(res => {
          const data = Array.isArray(res.data) ? res.data : [];
          setAvailablePlans(data);
        })
        .catch(err => console.error("Failed to fetch subscription plans:", err));
    }
  }, [isOpen]);

  const toggleTopic = (topic: string) => {
    setFormData(prev => ({
      ...prev,
      topics: prev.topics.includes(topic) 
        ? prev.topics.filter(t => t !== topic)
        : [...prev.topics, topic]
    }));
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setIsCreatingCategory(false);
      return;
    }
    try {
      const res = await api.post("/categories", { name: newCategoryName.trim() });
      if (res.data && res.data.category) {
        setCategories(prev => [...prev, res.data.category].sort((a, b) => a.name.localeCompare(b.name)));
        setFormData(prev => ({ ...prev, categoryId: res.data.category.id }));
        showToast("Category Added", "New category created successfully.", "success");
      }
    } catch (err) {
      console.error("Failed to create category", err);
      showToast("Creation Failed", "Failed to create category.", "error");
    } finally {
      setIsCreatingCategory(false);
      setNewCategoryName("");
    }
  };

  const processTopics = (text: string) => {
    const rawItems = text.split(/[\n,]+/);
    const cleanedItems = rawItems
      .map(item => {
        let cleaned = item.trim();
        cleaned = cleaned.replace(/^[\s\-\*\•\d\.\)]+\s*/, '');
        cleaned = cleaned.replace(/^[\p{Emoji}\s]+/gu, '');
        cleaned = cleaned.replace(/[\p{Emoji}\s]+$/gu, '');
        return cleaned.trim();
      })
      .filter(item => item.length > 0);

    if (cleanedItems.length === 0) return;

    let addedTopics: string[] = [];
    
    setFormData(prev => {
      const currentTopics = [...prev.topics];
      cleanedItems.forEach(nt => {
        const existsAvailable = availableTopics.find(at => at.toLowerCase() === nt.toLowerCase());
        const existsCurrent = currentTopics.find(ct => ct.toLowerCase() === nt.toLowerCase());
        
        const finalTopicName = existsAvailable || nt;
        if (!existsCurrent) {
          currentTopics.push(finalTopicName);
          addedTopics.push(finalTopicName);
        }
      });
      return { ...prev, topics: currentTopics };
    });
    
    setTimeout(() => {
      if (addedTopics.length > 0) {
        setAvailableTopics(prev => {
          const updated = [...prev];
          addedTopics.forEach(at => {
            if (!updated.includes(at)) updated.push(at);
          });
          return updated;
        });
      }
    }, 0);
  };

  const handleAddQuickTopics = () => {
    if (!quickTopicInput.trim()) return;
    processTopics(quickTopicInput);
    setQuickTopicInput("");
  };

  const handleSmartPasteTopics = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText.includes('\n') || pastedText.includes(',')) {
      e.preventDefault();
      processTopics(pastedText);
    }
  };

  const updateLearningPoint = (index: number, value: string) => {
    const updated = [...formData.what_youll_learn];
    updated[index] = value;
    if (index === updated.length - 1 && value.trim() !== "") {
      updated.push("");
    }
    setFormData({ ...formData, what_youll_learn: updated });
  };

  const removeLearningPoint = (index: number) => {
    if (formData.what_youll_learn.length === 1) {
      setFormData({ ...formData, what_youll_learn: [""] });
      return;
    }
    const updated = formData.what_youll_learn.filter((_, i) => i !== index);
    setFormData({ ...formData, what_youll_learn: updated });
  };

  const addLearningPoint = () => {
    setFormData(prev => ({
      ...prev,
      what_youll_learn: [...prev.what_youll_learn, ""]
    }));
  };

  const handlePasteLearningPoint = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const items = pastedText.split(/[\n;]+/).map(item => item.replace(/^[•\-"\s]+/, '').replace(/["\s]+$/, '').trim()).filter(item => item !== "");
    
    if (items.length > 0) {
      const updated = [...formData.what_youll_learn];
      updated[index] = items[0];
      const tail = updated.splice(index + 1);
      
      const combined = [...updated, ...items.slice(1), ...tail];
      
      if (combined[combined.length - 1] !== "") {
        combined.push("");
      }
      
      setFormData({ ...formData, what_youll_learn: combined });
    }
  };

  const handleSubmit = (e: React.FormEvent, status: string = "PUBLISHED") => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      showToast("Missing Information", "Please provide a Course Title before saving.", "error");
      return;
    }
    
    const cleanedFormData = {
      ...formData,
      status: status,
      what_youll_learn: formData.what_youll_learn.filter(p => p.trim() !== "")
    };

    if (initialData && onUpdate) {
      const updatedCourse = {
        ...initialData,
        ...cleanedFormData,
      };
      onUpdate(updatedCourse);
    } else {
      const newCourse = {
        ...cleanedFormData,
        id: `c-${Date.now()}`,
        completionCount: 0,
        enrollmentCount: 0,
        creator: "Coach Arjun",
        progress: 0,
        createdAt: new Date().toISOString().split('T')[0]
      };
      onCreate(newCourse);
    }

    onClose();
  };

  const copyCourseSampleJsonToClipboard = async () => {
    const sample = {
      _AI_INSTRUCTIONS_: [
        "Generate a new course.",
        "The course description and learning objectives must be 'Modern Malayalam + Simple English'.",
        "For 'categoryName', choose from the existing lists OR create new names.",
        "Provide the final response STRICTLY as raw, valid JSON text matching this exact structure. Do not use markdown blocks (```json) or conversational text so I can copy it directly."
      ],
      title: "Sample Course Title",
      description: "Detailed description of what the course covers.",
      publishDate: new Date().toISOString().split('T')[0],
      videoLink: "https://youtube.com/watch?v=sample",
      highlightsLink: "https://youtube.com/watch?v=highlights",
      categoryName: "SaaS Growth",
      currently_available_categories: categories.map(c => c.name),
      topics: ["Marketing", "Sales"],
      learning_order: "1",
      estimated_duration_minutes: "45",
      difficulty_level: "beginner",
      what_youll_learn: ["Understand core concepts", "Implement strategies effectively"],
      feedback_links: ["https://facebook.com/groups/sample"]
    };
    
    const textToCopy = JSON.stringify(sample, null, 2);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for insecure HTTP contexts
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        textArea.remove();
        if (!successful) throw new Error("document.execCommand failed");
      }
      showToast("Success", "AI JSON template copied to clipboard", "success");
    } catch (err) {
      console.error("Clipboard Copy Error:", err);
      showToast("Error", "Failed to copy to clipboard", "error");
    }
  };

  const handleCoursePasteFromClipboard = async () => {
    try {
      let text = "";
      if (navigator.clipboard && window.isSecureContext) {
        try {
          text = await navigator.clipboard.readText();
        } catch (e) {
          text = window.prompt("Your browser blocked direct clipboard access. Please paste (Ctrl+V) the AI JSON here:") || "";
        }
      } else {
        text = window.prompt("Your browser blocked direct clipboard access. Please paste (Ctrl+V) the AI JSON here:") || "";
      }
      
      if (!text) {
        showToast("Error", "Clipboard/Input is empty", "error");
        return;
      }
      
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      const cleanedText = jsonMatch ? jsonMatch[0] : text;
      
      const json = JSON.parse(cleanedText);
      
      let finalCategoryId = json.categoryId;
      const categorySearch = (json.categoryId || json.category || json.categoryName || "").toString().trim().toLowerCase();
      
      if (categorySearch) {
        const match = categories.find(c => c.id === categorySearch || c.name.toLowerCase() === categorySearch);
        if (match) {
          finalCategoryId = match.id;
        } else {
           try {
             const rawName = (json.categoryId || json.category || json.categoryName).toString().trim();
             const res = await api.post("/categories", { name: rawName });
             if (res.data && res.data.category) {
               setCategories(prevCats => [...prevCats, res.data.category].sort((a, b) => a.name.localeCompare(b.name)));
               finalCategoryId = res.data.category.id;
               showToast("Category Added", `Auto-created category "${res.data.category.name}".`, "success");
             }
           } catch(err) {
             console.warn("Could not auto-create category");
           }
        }
      }
      
      setFormData(prev => ({
        ...prev,
        title: json.title || prev.title,
        description: json.description || prev.description,
        publishDate: json.publishDate || prev.publishDate,
        videoLink: json.videoLink || prev.videoLink,
        highlightsLink: json.highlightsLink || prev.highlightsLink,
        categoryId: finalCategoryId || prev.categoryId,
        topics: Array.isArray(json.topics) ? json.topics : prev.topics,
        learning_order: json.learning_order?.toString() || prev.learning_order,
        estimated_duration_minutes: json.estimated_duration_minutes?.toString() || prev.estimated_duration_minutes,
        difficulty_level: json.difficulty_level || prev.difficulty_level,
        what_youll_learn: Array.isArray(json.what_youll_learn) ? json.what_youll_learn : prev.what_youll_learn,
        feedback_links: Array.isArray(json.feedback_links) ? json.feedback_links : prev.feedback_links
      }));
      showToast("Success", "Course form populated from JSON");
      
      if (Array.isArray(json.topics)) {
         setAvailableTopics(prevTopics => {
           const updated = [...prevTopics];
           json.topics.forEach((t: string) => {
             if (!updated.includes(t)) updated.push(t);
           });
           return updated;
         });
      }
    } catch (err) {
      console.error(err);
      showToast("Error", "Invalid JSON in clipboard", "error");
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={initialData ? "Update Course Properties" : "Define New Course"}>
      <div className="flex gap-2 justify-end mb-2 px-1">
        <button 
          type="button" 
          onClick={copyCourseSampleJsonToClipboard}
          className="text-[10px] font-black uppercase text-primary bg-primary/10 hover:bg-primary/20 px-3 py-2 rounded-lg tracking-widest flex items-center gap-1 transition-colors"
        >
          <span className="material-symbols-outlined text-sm">content_copy</span>
          Copy AI JSON
        </button>
        <button 
          type="button"
          onClick={handleCoursePasteFromClipboard}
          className="text-[10px] font-black uppercase text-on-surface bg-surface-container-highest hover:bg-surface-container-high px-3 py-2 rounded-lg tracking-widest flex items-center gap-1 transition-colors border border-outline-variant/10"
        >
          <span className="material-symbols-outlined text-sm">content_paste</span>
          Paste from Clipboard
        </button>
      </div>
      <form onSubmit={(e) => handleSubmit(e, "PUBLISHED")} className="space-y-6 pt-2">
        <div className="space-y-4">
          
          {/* Access Tags / Subscription Plan Locks */}
          <div className="space-y-3 bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30">
            <label className="text-[10px] font-black uppercase text-blue-500 tracking-widest pl-2">Access Locks (Premium Plans)</label>
            <p className="text-xs text-slate-500 pl-2 mb-2">Leave all unchecked to make this course BASIC (Available to all users). Check plans to lock the course behind them.</p>
            <div className="grid grid-cols-2 gap-3 pl-2">
              {availablePlans.map((plan) => (
                <label key={plan.id} className="flex items-center space-x-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={formData.access_tags.includes(plan.id)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          access_tags: isChecked 
                            ? [...prev.access_tags, plan.id]
                            : prev.access_tags.filter(id => id !== plan.id)
                        }));
                      }}
                    />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      formData.access_tags.includes(plan.id)
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 group-hover:border-blue-400'
                    }`}>
                      {formData.access_tags.includes(plan.id) && <span className="material-symbols-outlined text-[14px]">check</span>}
                    </div>
                  </div>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                    {plan.name}
                  </span>
                </label>
              ))}
              {availablePlans.length === 0 && (
                <div className="col-span-2 text-xs text-orange-500 italic">No plans found. Please create plans in Admin Subscriptions first.</div>
              )}
            </div>
          </div>

          {/* Release Date */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Release Date</label>
            <input 
              required
              type="date"
              className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              value={formData.publishDate}
              onChange={(e) => setFormData({...formData, publishDate: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Learning Order */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Learning Order</label>
              <input 
                type="number"
                step="0.001"
                placeholder="e.g. 1.000 (Leave blank for auto)"
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                value={formData.learning_order}
                onChange={(e) => setFormData({...formData, learning_order: e.target.value})}
              />
            </div>

            {/* Estimated Duration */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Duration (mins)</label>
              <input 
                type="number"
                placeholder="e.g. 45"
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                value={formData.estimated_duration_minutes}
                onChange={(e) => setFormData({...formData, estimated_duration_minutes: e.target.value})}
              />
            </div>
          </div>

          {/* Course Category */}
          <div className="space-y-2">
            <div className="flex items-center justify-between pl-2 pr-1">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Course Category</label>
              {!isCreatingCategory && (
                <button
                  type="button"
                  onClick={() => setIsCreatingCategory(true)}
                  className="text-[9px] font-black uppercase text-primary hover:text-primary/80 tracking-widest flex items-center gap-1 transition-colors"
                >
                  <span className="material-symbols-outlined text-[10px]">add</span>
                  New
                </button>
              )}
            </div>

            {isCreatingCategory ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Category Name..."
                  className="flex-1 px-4 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateCategory();
                    } else if (e.key === 'Escape') {
                      setIsCreatingCategory(false);
                    }
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleCreateCategory}
                  className="px-4 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 flex items-center justify-center"
                  title="Save Category"
                >
                  <span className="material-symbols-outlined text-sm">check</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsCreatingCategory(false)}
                  className="px-4 bg-surface-container-high text-on-surface-variant hover:text-on-surface rounded-xl hover:bg-surface-container-highest transition-colors flex items-center justify-center"
                  title="Cancel"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
            ) : (
              <div className="relative">
                <select
                  className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm appearance-none cursor-pointer text-on-surface"
                  value={formData.categoryId}
                  onChange={(e) => setFormData({...formData, categoryId: e.target.value})}
                >
                  <option value="" className="bg-surface-container-low text-on-surface-variant">-- Select Category --</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id} className="bg-surface-container-low text-on-surface">
                      {cat.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-5 text-on-surface-variant/70">
                  <span className="material-symbols-outlined text-lg">keyboard_arrow_down</span>
                </div>
              </div>
            )}
          </div>


          {/* Difficulty Level (Class) */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Class (Difficulty Level)</label>
            <div className="flex bg-surface-container-high p-1.5 rounded-xl border border-outline-variant/10">
              {(['beginner', 'intermediate', 'advanced'] as const).map((level) => (
                <label key={level} className="flex-1 text-center relative cursor-pointer">
                  <input
                    type="radio"
                    name="difficulty_level"
                    value={level}
                    checked={formData.difficulty_level === level}
                    onChange={(e) => setFormData({...formData, difficulty_level: e.target.value})}
                    className="sr-only"
                  />
                  <div className={`py-2.5 px-2 rounded-lg text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
                    formData.difficulty_level === level 
                      ? 'bg-primary text-white shadow-md' 
                      : 'text-on-surface-variant hover:bg-surface-container-highest'
                  }`}>
                    <span>
                      {level === 'beginner' && '🟢'}
                      {level === 'intermediate' && '🟡'}
                      {level === 'advanced' && '🔴'}
                    </span>
                    <span className="capitalize">{level}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Course Title</label>
            <input 
              required
              type="text" 
              placeholder="e.g. Scaling Your SaaS to ₹1M"
              className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Value Proposition / Description</label>
            <textarea 
              required
              rows={3}
              placeholder="What will students achieve after this course?"
              className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium text-sm resize-none"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          {/* What You'll Learn */}
          <div className="space-y-3">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2 flex justify-between items-center">
              <span>Curriculum Topics (What You'll Learn)</span>
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar p-1">
              {formData.what_youll_learn.map((point, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g. Understand why customers ask for the price first."
                    className="flex-1 px-4 py-2.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                    value={point}
                    onChange={(e) => updateLearningPoint(index, e.target.value)}
                    onPaste={(e) => handlePasteLearningPoint(index, e)}
                  />
                  <button 
                    type="button" 
                    onClick={() => removeLearningPoint(index)}
                    disabled={index === formData.what_youll_learn.length - 1 && !point.trim()}
                    className={`p-2.5 rounded-xl transition-colors ${
                      (index === formData.what_youll_learn.length - 1 && !point.trim())
                        ? "text-slate-300 opacity-50 cursor-default"
                        : "text-error hover:bg-error/10"
                    }`}
                  >
                    <span className="material-symbols-outlined text-xl">delete</span>
                  </button>
                </div>
              ))}
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={addLearningPoint}
                  className="text-[10px] font-black uppercase text-primary hover:text-primary/80 tracking-widest flex items-center gap-1 transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">add</span>
                  Add Point
                </button>
              </div>
            </div>
          </div>

          {/* Topic Selection */}
          <div className="space-y-3">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Related Topics (Target Topics)</label>
            
            {/* Selected Topics Area */}
            {formData.topics.length > 0 && (
              <div className="space-y-2 bg-primary/5 p-3 sm:p-4 rounded-2xl border border-primary/10">
                <p className="text-[9px] font-black uppercase text-primary tracking-widest">Selected Topics</p>
                <div className="flex flex-wrap gap-2">
                  {formData.topics.map(topic => (
                    <button
                      key={topic}
                      type="button"
                      onClick={() => toggleTopic(topic)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-tight transition-all border bg-primary text-on-primary border-primary shadow-lg shadow-primary/20 hover:bg-red-500 hover:border-red-500 hover:shadow-red-500/20 group"
                      title="Remove Topic"
                    >
                      {topic}
                      <span className="material-symbols-outlined text-[12px] opacity-70 group-hover:opacity-100">close</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Available Topics Area */}
            <div className="space-y-2 p-3 sm:p-4 rounded-2xl border border-outline-variant/10 bg-surface-container-lowest">
              <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Available Suggestions</p>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto custom-scrollbar pr-1">
                {availableTopics.filter(t => !formData.topics.includes(t)).map(topic => (
                  <button
                    key={topic}
                    type="button"
                    onClick={() => toggleTopic(topic)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-tight transition-all border bg-surface-container-high text-on-surface-variant border-outline-variant/10 hover:border-primary/30 hover:text-primary"
                  >
                    <span className="material-symbols-outlined text-[12px] opacity-70">add</span>
                    {topic}
                  </button>
                ))}
                {availableTopics.filter(t => !formData.topics.includes(t)).length === 0 && (
                  <p className="text-xs text-slate-400 italic py-2 px-1">No remaining suggestions.</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Quick add topics (comma separated)..."
                className="flex-1 px-4 py-2.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-xs"
                value={quickTopicInput}
                onChange={(e) => setQuickTopicInput(e.target.value)}
                onPaste={handleSmartPasteTopics}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddQuickTopics();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddQuickTopics}
                className="px-4 py-2.5 bg-primary/10 text-primary hover:bg-primary/20 transition-colors rounded-xl font-black text-[10px] uppercase tracking-wider whitespace-nowrap"
              >
                Add Topics
              </button>
            </div>
          </div>

          {/* Highlights Link */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Highlights Video Link (YT)</label>
            <div className="flex flex-col gap-3">
              <input 
                type="url"
                placeholder="https://youtube.com/..."
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                value={formData.highlightsLink}
                onChange={(e) => setFormData({...formData, highlightsLink: e.target.value})}
              />
              {getYoutubeId(formData.highlightsLink) && (
                <div className="w-full aspect-video rounded-xl overflow-hidden border border-outline-variant/10 bg-surface-container-highest shadow-md relative group">
                  <img 
                    src={`https://img.youtube.com/vi/${getYoutubeId(formData.highlightsLink)}/maxresdefault.jpg`} 
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${getYoutubeId(formData.highlightsLink)}/hqdefault.jpg`;
                    }}
                    alt="Preview" 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
                    <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <span className="material-symbols-outlined text-2xl ml-1">play_arrow</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Master Video Link */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Master Video Link (YT)</label>
            <div className="flex flex-col gap-3">
              <input 
                required
                type="url"
                placeholder="https://youtube.com/..."
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                value={formData.videoLink}
                onChange={(e) => setFormData({...formData, videoLink: e.target.value})}
              />
              {getYoutubeId(formData.videoLink) && (
                <div className="w-full aspect-video rounded-xl overflow-hidden border border-outline-variant/10 bg-surface-container-highest shadow-md relative group">
                  <img 
                    src={`https://img.youtube.com/vi/${getYoutubeId(formData.videoLink)}/maxresdefault.jpg`} 
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${getYoutubeId(formData.videoLink)}/hqdefault.jpg`;
                    }}
                    alt="Preview" 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
                    <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <span className="material-symbols-outlined text-2xl ml-1">play_arrow</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Feedback Links */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Social Feedback Links</label>
              <button 
                type="button"
                onClick={() => setFormData({...formData, feedback_links: [...formData.feedback_links, ""]})}
                className="text-[10px] font-black uppercase tracking-[0.2em] text-primary hover:text-primary-variant transition-colors flex items-center gap-1 bg-primary/10 px-3 py-1.5 rounded-lg"
              >
                <span className="material-symbols-outlined text-[14px]">add</span> Add Link
              </button>
            </div>
            
            {formData.feedback_links.length === 0 && (
              <div className="text-center py-6 bg-surface-container border border-dashed border-outline-variant/10 rounded-2xl">
                <span className="material-symbols-outlined text-3xl text-on-surface-variant/30 mb-2">reviews</span>
                <p className="text-xs font-bold text-on-surface-variant">No feedback links added</p>
                <p className="text-[10px] text-on-surface-variant/60 mt-1">These will appear when a student completes the course</p>
              </div>
            )}
            
            <div className="space-y-3">
              {formData.feedback_links.map((url, index) => (
                <div key={index} className="flex gap-2 items-start bg-surface-container-high p-3 rounded-2xl border border-outline-variant/5 relative group">
                  <div className="flex-1">
                    <input 
                      type="url"
                      placeholder="https://facebook.com/..."
                      className="w-full px-4 py-3 bg-surface-container-highest text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium text-xs font-mono"
                      value={url}
                      onChange={(e) => {
                        const newLinks = [...formData.feedback_links];
                        newLinks[index] = e.target.value;
                        setFormData({...formData, feedback_links: newLinks});
                      }}
                    />
                  </div>
                  <button 
                    type="button"
                    onClick={() => {
                      const newLinks = [...formData.feedback_links];
                      newLinks.splice(index, 1);
                      setFormData({...formData, feedback_links: newLinks});
                    }}
                    className="p-3 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500/20 transition-colors self-stretch flex items-center justify-center opacity-0 group-hover:opacity-100"
                    title="Remove Link"
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
          
        </div>

        <div className="pt-4 border-t border-outline-variant/10 flex gap-3 sm:gap-4">
          <button 
            type="button"
            onClick={onClose}
            className="flex-1 py-3.5 bg-surface-container-high text-on-surface-variant rounded-xl font-black text-xs uppercase tracking-widest"
          >
            Discard
          </button>
          
          <button 
            type="button"
            onClick={(e) => handleSubmit(e, "DRAFT")}
            className="flex-1 py-3.5 bg-surface-container-highest text-on-surface-variant rounded-xl font-black text-xs uppercase tracking-widest hover:text-on-surface transition-colors"
          >
            Save Draft
          </button>
          <button 
            type="submit"
            className="flex-[2] py-3.5 bg-primary text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            {initialData ? "Save Changes" : "Publish"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
